import type { ComponentType } from 'react';
import { Anim, Between, Handles, keys, outline, Walk } from '../stage';

/**
 * The deform group: liquify's seven brushes, Free Transform's three shapings and Pin Warp,
 * the Shape Builder, the Cloner and Add-ons.
 *
 * Every outline that bends is sampled as points and bent in JS, then animated between
 * `outline(before)` and `outline(after)` — the same count of points both ways, so SMIL can
 * interpolate it (see TwirlDemo). A bend that is linear in how far the hand has gone needs
 * two keyframes; one that trails the brush (the edge textures) is keyed along the sweep.
 */

type P = [number, number];
type Quad = [P, P, P, P];

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const clamp = (v: number, lo = 0, hi = 1) => Math.min(hi, Math.max(lo, v));

/** A circle as `n` points. */
function ring(cx: number, cy: number, r: number, n: number): P[] {
  return Array.from({ length: n }, (_, i) => {
    const a = (i / n) * Math.PI * 2;
    return [cx + Math.cos(a) * r, cy + Math.sin(a) * r] as P;
  });
}

/**
 * A rounded rectangle as points, clockwise from the top left: the top side every `top`
 * units, the others every `step`. `onTop[i]` says whether point i is on the straight top.
 */
function roundRect(x0: number, y0: number, x1: number, y1: number, r: number, step: number, top = step) {
  const pts: P[] = [];
  const onTop: boolean[] = [];
  const side = (ax: number, ay: number, bx: number, by: number, every: number, isTop = false) => {
    const n = Math.max(1, Math.round(Math.hypot(bx - ax, by - ay) / every));
    for (let i = 0; i < n; i++) { pts.push([lerp(ax, bx, i / n), lerp(ay, by, i / n)]); onTop.push(isTop); }
  };
  const arc = (cx: number, cy: number, a0: number) => {
    for (let i = 0; i < 6; i++) {
      const a = a0 + (i / 6) * (Math.PI / 2);
      pts.push([cx + Math.cos(a) * r, cy + Math.sin(a) * r]);
      onTop.push(false);
    }
  };
  side(x0 + r, y0, x1 - r, y0, top, true); arc(x1 - r, y0 + r, -Math.PI / 2);
  side(x1, y0 + r, x1, y1 - r, step); arc(x1 - r, y1 - r, 0);
  side(x1 - r, y1, x0 + r, y1, step); arc(x0 + r, y1 - r, Math.PI / 2);
  side(x0, y1 - r, x0, y0 + r, step); arc(x0 + r, y0 + r, Math.PI);
  return { pts, onTop };
}

/** Hold the brush on the shape, the outline bends, the brush leaves: the twirl's pacing. */
function HeldBrush({ k, before, after, cx, cy, r }: { k: string; before: P[]; after: P[]; cx: number; cy: number; r: number }) {
  return <>
    <path className="g-new" d={outline(before)}>
      <Anim attr="d" values={[outline(before), outline(before), outline(after), outline(after)]} times={[0, 0.25, 0.7, 1]} />
    </path>
    <circle className="g-brush" cx={cx} cy={cy} r={r}>
      <Between from={0.2} to={0.74} />
    </circle>
    <Walk k={k} stops={[[204, 98, 0.04], [cx, cy, 0.22], [cx, cy, 0.74], [206, 22, 0.9]]} />
  </>;
}

/** Warp: take the edge and drag — the outline is pushed along with the brush. */
function WarpDemo() {
  const R = 24;
  const from: P = [118, 56];
  const to: P = [170, 44];
  const before = ring(90, 56, 28, 80);
  const after = before.map(([x, y]) => {
    const d = Math.hypot(x - from[0], y - from[1]);
    const f = d < R ? (1 - (d / R) ** 2) ** 2 : 0;
    return [x + (to[0] - from[0]) * f, y + (to[1] - from[1]) * f] as P;
  });
  const times = [0, 0.24, 0.66, 1];
  return <>
    <path className="g-new" d={outline(before)}>
      <Anim attr="d" values={[outline(before), outline(before), outline(after), outline(after)]} times={times} />
    </path>
    <circle className="g-brush" r={R}>
      <Anim attr="cx" values={[from[0], from[0], to[0], to[0]]} times={times} />
      <Anim attr="cy" values={[from[1], from[1], to[1], to[1]]} times={times} />
      <Between from={0.18} to={0.72} />
    </circle>
    <Walk k="liquify:warp" stops={[[200, 98, 0.04], [from[0], from[1], 0.2], [from[0], from[1], 0.24], [to[0], to[1], 0.66], [to[0], to[1], 0.72], [212, 90, 0.88]]} />
  </>;
}

/** The pill Pinch and Swell work on, with the brush held on its middle. */
const PILL = roundRect(34, 43, 206, 69, 13, 2.5).pts;
const MID: P = [120, 56];

/** Pinch: the outline under the brush is drawn in toward its centre — a waist. */
function PuckerDemo() {
  const R = 36;
  const after = PILL.map(([x, y]) => {
    const d = Math.hypot(x - MID[0], y - MID[1]);
    const f = d < R ? 0.92 * (1 - (d / R) ** 2) : 0;
    return [x + (MID[0] - x) * f, y + (MID[1] - y) * f] as P;
  });
  return <HeldBrush k="liquify:pucker" before={PILL} after={after} cx={MID[0]} cy={MID[1]} r={R} />;
}

/** Swell: the outline under the brush is pushed out from its centre — a bulge. */
function BloatDemo() {
  const R = 36;
  const after = PILL.map(([x, y]) => {
    const dx = x - MID[0];
    const dy = y - MID[1];
    const d = Math.hypot(dx, dy) || 1;
    const f = d < R ? (1 - (d / R) ** 2) ** 1.5 : 0;
    return [x + (dx / d) * 22 * f, y + (dy / d) * 22 * f] as P;
  });
  return <HeldBrush k="liquify:bloat" before={PILL} after={after} cx={MID[0]} cy={MID[1]} r={R} />;
}

/**
 * The edge textures: the brush runs along the top of a card and the texture forms behind
 * it. `tex(s)` is how far the edge moves down (negative: up) `s` units along the top.
 */
function EdgeTexture({ k, tex }: { k: string; tex: (s: number) => number }) {
  const x0 = 50;
  const x1 = 190;
  const y0 = 38;
  const r = 8;
  const R = 15;
  const { pts, onTop } = roundRect(x0, y0, x1, 86, r, 8, 1);
  const start = x0 + r;
  const end = x1 - r;
  const at = (bx: number) => outline(pts.map(([x, y], i) => {
    if (!onTop[i]) return [x, y] as P;
    const trail = clamp((bx - x) / R + 0.4);
    const ends = clamp((x - start) / 5) * clamp((end - x) / 5);
    return [x, y + tex(x - start) * trail * ends] as P;
  }));
  const from = 52;
  const to = 198;
  const t0 = 0.2;
  const t1 = 0.7;
  const steps = 12;
  const values = [at(-100)];
  const times = [0];
  for (let i = 0; i <= steps; i++) {
    values.push(at(lerp(from, to, i / steps)));
    times.push(Number(lerp(t0, t1, i / steps).toFixed(4)));
  }
  values.push(values.at(-1)!);
  times.push(1);
  values[1] = values[0]!; // before the brush arrives the edge is plain
  return <>
    <path className="g-new" d={values[0]}>
      <Anim attr="d" values={values} times={times} />
    </path>
    <circle className="g-brush" cy={y0} r={R}>
      <Anim attr="cx" values={[from, from, to, to]} times={[0, t0, t1, 1]} />
      <Between from={0.16} to={0.74} />
    </circle>
    <Walk k={k} stops={[[30, 100, 0.03], [from, y0, t0 - 0.02], [from, y0, t0], [to, y0, t1], [to, y0, 0.74], [214, 96, 0.9]]} />
  </>;
}

/** Scallop: rounded notches bitten into the edge. */
function ScallopDemo() {
  const P = 17;
  return <EdgeTexture k="liquify:scallop" tex={(s) => {
    const u = (s % P) / P;
    return 8 * Math.sqrt(Math.max(0, 1 - (2 * u - 1) ** 2));
  }} />;
}

/** Crystallize: sharp spikes thrown outward, of uneven height. */
function CrystallizeDemo() {
  const P = 12;
  const heights = [11, 7, 13, 8, 10, 14, 7, 12, 9, 13, 8, 11];
  return <EdgeTexture k="liquify:crystallize" tex={(s) => {
    const u = (s % P) / P;
    const h = heights[Math.floor(s / P) % heights.length]!;
    return -h * (1 - Math.abs(2 * u - 1)) ** 1.6;
  }} />;
}

/** Wrinkle: small, uneven folds, up and down. */
function WrinkleDemo() {
  const gaps = [4, 3, 5, 3, 4, 6, 3, 4, 5, 3, 4, 3, 5, 4, 3, 6];
  const amps = [3.4, -2.6, 4, -3.2, 2.2, -3.8, 3, -2, 3.6, -3, 2.6, -3.6, 3.2, -2.4, 3.8, -2.8];
  const knots: P[] = [[0, 0]];
  for (let i = 0; knots.at(-1)![0] < 140; i++) knots.push([knots.at(-1)![0] + gaps[i % gaps.length]!, amps[i % amps.length]!]);
  return <EdgeTexture k="liquify:wrinkle" tex={(s) => {
    const j = knots.findIndex(([x]) => x > s);
    if (j <= 0) return 0;
    const [ax, ay] = knots[j - 1]!;
    const [bx, by] = knots[j]!;
    return lerp(ay, by, (s - ax) / (bx - ax));
  }} />;
}

/** A point on a quad, by where it is on the unit square (bilinear). */
function onQuad(q: Quad, u: number, v: number): P {
  const top: P = [lerp(q[0][0], q[1][0], u), lerp(q[0][1], q[1][1], u)];
  const bottom: P = [lerp(q[3][0], q[2][0], u), lerp(q[3][1], q[2][1], u)];
  return [lerp(top[0], bottom[0], v), lerp(top[1], bottom[1], v)];
}

const boxOf = (x0: number, y0: number, x1: number, y1: number): Quad => [[x0, y0], [x1, y0], [x1, y1], [x0, y1]];

/**
 * Free Transform: a chosen card (a panel with a disc on it) in its box; the hand takes
 * `grab` and the corners go from `from` to `to`, the card following the box.
 */
function Shaping({ k, from, to, grab, approach }: { k: string; from: Quad; to: Quad; grab: number; approach: P }) {
  const disc = (q: Quad) => outline(Array.from({ length: 40 }, (_, i) => {
    const a = (i / 40) * Math.PI * 2;
    return onQuad(q, 0.5 + Math.cos(a) * 0.24, 0.5 + Math.sin(a) * 0.3);
  }));
  const times = [0, 0.26, 0.66, 1];
  const both = <T,>(f: (q: Quad) => T) => [f(from), f(from), f(to), f(to)];
  return <>
    <path className="g-new" d={outline(from)}><Anim attr="d" values={both((q) => outline(q))} times={times} /></path>
    <path className="g-new" d={disc(from)}><Anim attr="d" values={both(disc)} times={times} /></path>
    <path className="g-sel" d={outline(from)}><Anim attr="d" values={both((q) => outline(q))} times={times} /></path>
    {from.map((_, i) => (
      <rect key={i} className="g-handle" width={5} height={5} x={from[i]![0] - 2.5} y={from[i]![1] - 2.5}
        style={i === grab ? { fill: 'var(--accent)' } : undefined}>
        <Anim attr="x" values={both((q) => q[i]![0] - 2.5)} times={times} />
        <Anim attr="y" values={both((q) => q[i]![1] - 2.5)} times={times} />
      </rect>
    ))}
    <Walk k={k} stops={[[approach[0], approach[1], 0.04], [from[grab]![0], from[grab]![1], 0.22], [from[grab]![0], from[grab]![1], 0.26], [to[grab]![0], to[grab]![1], 0.66]]} />
  </>;
}

/** Scale: drag a corner and the card grows about the corner opposite. */
function ScaleDemo() {
  const from = boxOf(66, 26, 118, 62);
  const s = 1.55;
  const to = from.map(([x, y]) => [66 + (x - 66) * s, 26 + (y - 26) * s] as P) as Quad;
  return <Shaping k="warp:transform" from={from} to={to} grab={2} approach={[204, 100]} />;
}

/** Perspective: drag a top corner in; its partner comes in to meet it — a trapezium. */
function PerspectiveDemo() {
  const from = boxOf(76, 24, 164, 88);
  const to: Quad = [[100, 24], [140, 24], [164, 88], [76, 88]];
  return <Shaping k="warp:perspective" from={from} to={to} grab={1} approach={[216, 72]} />;
}

/** Distort: one corner goes where the hand takes it; the other three stay. */
function DistortDemo() {
  const from = boxOf(74, 30, 150, 88);
  const to: Quad = [[74, 30], [188, 12], [150, 88], [74, 88]];
  return <Shaping k="warp:distort" from={from} to={to} grab={1} approach={[210, 96]} />;
}

/** Pin warp: three pins go in, the last is pulled, and the bar bends round the others. */
function PuppetDemo() {
  const pins: P[] = [[60, 58], [118, 58], [176, 58]];
  const pull: P = [-8, -34];
  const bar = roundRect(38, 48, 198, 68, 10, 2.5).pts;
  const bent = bar.map(([x, y]) => {
    let sum = 0;
    let dx = 0;
    let dy = 0;
    pins.forEach(([px, py], i) => {
      const w = 1 / (Math.hypot(x - px, y - py) ** 2 + 4) ** 1.2;
      sum += w;
      if (i === 2) { dx += w * pull[0]; dy += w * pull[1]; }
    });
    return [x + dx / sum, y + dy / sum] as P;
  });
  const times = [0, 0.42, 0.7, 1];
  const last = pins[2]!;
  return <>
    <path className="g-new" d={outline(bar)}>
      <Anim attr="d" values={[outline(bar), outline(bar), outline(bent), outline(bent)]} times={times} />
    </path>
    <g><Between from={0.12} to={1} /><circle className="g-anchor" cx={pins[0]![0]} cy={pins[0]![1]} r={3.2} /></g>
    <g><Between from={0.24} to={1} /><circle className="g-anchor" cx={pins[1]![0]} cy={pins[1]![1]} r={3.2} /></g>
    <circle className="g-anchor g-anchor-on" cx={last[0]} cy={last[1]} r={3.2}>
      <Between from={0.36} to={1} />
      <Anim attr="cx" values={[last[0], last[0], last[0] + pull[0], last[0] + pull[0]]} times={times} />
      <Anim attr="cy" values={[last[1], last[1], last[1] + pull[1], last[1] + pull[1]]} times={times} />
    </circle>
    <Walk k="warp:puppet" stops={[
      [26, 100, 0.02], [pins[0]![0], pins[0]![1], 0.1], [pins[0]![0], pins[0]![1], 0.14],
      [pins[1]![0], pins[1]![1], 0.22], [pins[1]![0], pins[1]![1], 0.26],
      [last[0], last[1], 0.34], [last[0], last[1], 0.42], [last[0] + pull[0], last[1] + pull[1], 0.7],
    ]} />
  </>;
}

/**
 * Shape builder: two overlapping discs are three pieces. A loop drawn round them picks
 * all three, they light up, and they are merged into one outline.
 */
function BuilderDemo() {
  // Discs at (100, 56) and (140, 56), radius 26, crossing at x 120.
  const top = '120 39.39';
  const bottom = '120 72.61';
  const pieces = [
    `M${top}A26 26 0 1 0 ${bottom}A26 26 0 0 1 ${top}Z`,
    `M${top}A26 26 0 0 1 ${bottom}A26 26 0 0 1 ${top}Z`,
    `M${top}A26 26 0 1 1 ${bottom}A26 26 0 0 0 ${top}Z`,
  ];
  const merged = `M${top}A26 26 0 1 0 ${bottom}A26 26 0 1 0 ${top}Z`;
  // The loop, by hand: round the pair, a little past where it began.
  const loop: P[] = [];
  const n = 30;
  for (let i = 0; i <= n; i++) {
    const a = Math.PI * 0.62 + (i / n) * Math.PI * 2.12;
    const wobble = 1 + 0.035 * Math.sin(a * 3 + 0.6);
    loop.push([120 + Math.cos(a) * 66 * wobble, 56 + Math.sin(a) * 38 * wobble]);
  }
  const lengths = [0];
  for (let i = 1; i < loop.length; i++) lengths.push(lengths[i - 1]! + Math.hypot(loop[i]![0] - loop[i - 1]![0], loop[i]![1] - loop[i - 1]![1]));
  const total = lengths.at(-1)!;
  const t0 = 0.12;
  const t1 = 0.5;
  const stops = loop.map(([x, y], i) => [x, y, Number((t0 + ((t1 - t0) * lengths[i]!) / total).toFixed(4))] as const);
  const lit = { fill: 'color-mix(in srgb, var(--accent) 40%, transparent)' };
  return <>
    <g>
      <Anim attr="opacity" values={[1, 1, 0, 0, 1, 1]} times={[0, 0.62, 0.6201, 0.97, 0.9701, 1]} />
      <circle className="g-obj" cx={100} cy={56} r={26} />
      <circle className="g-obj" cx={140} cy={56} r={26} />
      {pieces.map((d) => <path key={d} d={d} style={lit}><Between from={0.48} to={0.62} /></path>)}
    </g>
    <path className="g-new" d={merged}><Between from={0.62} to={0.97} /></path>
    <path d={outline(loop).replace('Z', '')} pathLength={1} strokeDasharray="1 1"
      style={{ fill: 'none', stroke: 'var(--accent)', strokeWidth: 1.2, strokeLinecap: 'round', strokeLinejoin: 'round' }}>
      <Anim attr="stroke-dashoffset" values={[1, 1, 0, 0, 0]} times={[0, t0, t1, 0.62, 1]} />
      <Between from={t0} to={0.62} />
    </path>
    <Walk k="builder" stops={[[210, 100, 0.03], ...stops, [stops.at(-1)![0], stops.at(-1)![1], 0.6], [206, 92, 0.8]]} />
  </>;
}

/** Cloner: point at a shape and it becomes a grid of copies, one after another. */
function ClonerDemo() {
  const size = 16;
  const pitch = 26;
  const x0 = 60;
  const y0 = 21;
  const cells: P[] = [];
  for (let row = 0; row < 3; row++) for (let col = 0; col < 5; col++) cells.push([x0 + col * pitch, y0 + row * pitch]);
  return <>
    {cells.slice(1).map(([x, y], i) => {
      const at = 0.3 + i * 0.025;
      return (
        <g key={i}>
          <Between from={at} to={0.97} />
          <animateTransform attributeName="transform" type="translate" {...keys([`${x0 - x} ${y0 - y}`, `${x0 - x} ${y0 - y}`, '0 0', '0 0'], [0, at, at + 0.07, 1])} />
          <rect className="g-new" x={x} y={y} width={size} height={size} rx={4} />
        </g>
      );
    })}
    <rect className="g-new" x={x0} y={y0} width={size} height={size} rx={4} />
    <g><Between from={0.18} to={0.97} /><rect className="g-sel" x={x0} y={y0} width={size} height={size} /><Handles x={x0} y={y0} w={size} h={size} /></g>
    <Walk k="cloner" stops={[[40, 96, 0.04], [x0 + 9, y0 + 9, 0.16], [x0 + 9, y0 + 9, 0.26], [34, 92, 0.44]]} />
  </>;
}

/** Add-on: point at a shape and the add-on's effect spreads over it. */
function AddonDemo() {
  const cx = 120;
  const cy = 56;
  const r = 32;
  const click: P = [128, 62];
  const dots: { x: number; y: number; size: number; at: number }[] = [];
  for (let y = cy - r + 4; y <= cy + r - 3; y += 7) {
    for (let x = cx - r + 4; x <= cx + r - 3; x += 7) {
      const odd = Math.round((y - cy) / 7) % 2 !== 0;
      const dx = x + (odd ? 3.5 : 0);
      if (Math.hypot(dx - cx, y - cy) > r - 4) continue;
      const size = 0.9 + 2.3 * ((y - (cy - r)) / (2 * r));
      dots.push({ x: dx, y, size, at: 0.36 + (Math.hypot(dx - click[0], y - click[1]) / 60) * 0.24 });
    }
  }
  return <>
    <circle className="g-obj" cx={cx} cy={cy} r={r} />
    <circle cx={cx} cy={cy} r={r} style={{ fill: 'none', stroke: 'var(--accent)', strokeWidth: 1.5 }}><Between from={0.36} to={0.97} /></circle>
    {dots.map((d) => <circle key={`${d.x},${d.y}`} className="g-accent" cx={d.x} cy={d.y} r={d.size}><Between from={d.at} to={0.97} /></circle>)}
    <g><Between from={0.26} to={0.97} /><rect className="g-sel" x={cx - r} y={cy - r} width={2 * r} height={2 * r} /><Handles x={cx - r} y={cy - r} w={2 * r} h={2 * r} /></g>
    <Walk k="addon" stops={[[206, 100, 0.04], [click[0], click[1], 0.24], [click[0], click[1], 0.32], [204, 98, 0.5]]} />
  </>;
}

/** Guides for the deform group, by key (see ../index.ts). */
export const DEFORM_DEMOS: Record<string, ComponentType> = {
  liquify: WarpDemo,
  'liquify:warp': WarpDemo,
  'liquify:pucker': PuckerDemo,
  'liquify:bloat': BloatDemo,
  'liquify:scallop': ScallopDemo,
  'liquify:crystallize': CrystallizeDemo,
  'liquify:wrinkle': WrinkleDemo,
  warp: ScaleDemo,
  'warp:transform': ScaleDemo,
  'warp:perspective': PerspectiveDemo,
  'warp:distort': DistortDemo,
  'warp:puppet': PuppetDemo,
  builder: BuilderDemo,
  cloner: ClonerDemo,
  addon: AddonDemo,
};
